// src/pages/PortailPublic/PortailFondsEpargnePages.tsx
//
// Août 2026 — Volet II du CDCF : l'espace adhérent du fonds d'épargne.
//
// Trois écrans montés sous `PortailECLayout`, donc sous la connexion, le
// menu et le pied de page EXISTANTS du portail abonné. Rien n'est dupliqué :
// l'authentification, le jeton et la déconnexion viennent du socle.
//
// Le ton diffère volontairement des autres onglets portail : un souscripteur
// ne doit rien au fonds, il lui confie de l'argent. Aucun écran ne parle donc
// d'échéance, de solde dû ni de paiement — on montre ce que son épargne est
// devenue, et de quoi le vérifier.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Wallet, TrendingUp, FileText, Download, Loader2, AlertCircle,
  RefreshCw, ShieldCheck, Landmark, Calendar,
} from 'lucide-react';
import {
  espaceClientService,
  SituationFonds, VersementFonds, DocumentFonds,
} from '../../services/espace-client.service';

// ─── Formatage ───────────────────────────────────────────────────────────────

const fmtMontant = (v: any): string =>
  `${(Number(v) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} FCFA`;

const fmtDate = (v: any): string => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('fr-FR'); } catch { return String(v); }
};

const messageErreur = (e: any, defaut: string): string =>
  e?.response?.data?.message || e?.message || defaut;

// ─── Briques partagées ───────────────────────────────────────────────────────

const Chargement: React.FC = () => (
  <div className="flex items-center justify-center py-16 text-gray-500 dark:text-slate-400 gap-2">
    <Loader2 className="w-5 h-5 animate-spin" /> Chargement…
  </div>
);

const Erreur: React.FC<{ message: string; onReessayer?: () => void }> = ({ message, onReessayer }) => (
  <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
    <div className="flex-1">
      <p>{message}</p>
      {onReessayer && (
        <button onClick={onReessayer} className="mt-2 inline-flex items-center gap-1.5 text-red-700 underline">
          <RefreshCw className="w-3.5 h-3.5" /> Réessayer
        </button>
      )}
    </div>
  </div>
);

const EnTete: React.FC<{ titre: string; sousTitre?: string }> = ({ titre, sousTitre }) => (
  <div className="mb-6">
    <h1 className="text-xl font-bold text-gray-900 dark:text-slate-100">{titre}</h1>
    {sousTitre && <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">{sousTitre}</p>}
  </div>
);

/** Hook de chargement — même comportement sur les trois écrans. */
function useChargement<T>(charger: () => Promise<T>, defaut: string) {
  const [donnees, setDonnees] = useState<T | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(true);

  const executer = useCallback(async () => {
    setEnCours(true);
    setErreur(null);
    try {
      setDonnees(await charger());
    } catch (e: any) {
      setErreur(messageErreur(e, defaut));
    } finally {
      setEnCours(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { executer(); }, [executer]);
  return { donnees, erreur, enCours, recharger: executer };
}

// ═══════════════════════════════════════════════════════════════════════════
// MA SITUATION
// ═══════════════════════════════════════════════════════════════════════════

export const PortailFondsSituation: React.FC = () => {
  const { donnees, erreur, enCours, recharger } =
    useChargement<SituationFonds>(() => espaceClientService.fondsMaSituation(), 'Situation indisponible');

  if (enCours) return <Chargement />;
  if (erreur) return <Erreur message={erreur} onReessayer={recharger} />;
  if (!donnees) return null;

  const { identite, avoirs, activite } = donnees;

  return (
    <div>
      <EnTete
        titre={`Bonjour, ${[identite.prenoms, identite.nom].filter(Boolean).join(' ')}`}
        sousTitre={[
          identite.numeroSouscripteur ? `N° ${identite.numeroSouscripteur}` : null,
          identite.entiteOrigine,
          identite.bureauRegional,
        ].filter(Boolean).join(' · ')}
      />

      {/* L'avoir total en premier : c'est la seule chose que l'adhérent vient voir. */}
      <div className="rounded-xl p-6 text-white bg-gradient-to-br from-blue-700 to-blue-900">
        <p className="text-xs uppercase tracking-wider opacity-80">
          Avoir total au {fmtDate(new Date())}
        </p>
        <p className="text-4xl font-bold mt-1 tabular-nums">{fmtMontant(avoirs.avoirTotal)}</p>
        <div className="flex flex-wrap gap-x-10 gap-y-3 mt-5 pt-5 border-t border-white/20">
          <div>
            <p className="text-xs opacity-80">Versements cumulés</p>
            <p className="font-semibold tabular-nums">{fmtMontant(avoirs.capitalVerse)}</p>
          </div>
          <div>
            <p className="text-xs opacity-80">Intérêts capitalisés</p>
            <p className="font-semibold tabular-nums">{fmtMontant(avoirs.interetsCumules)}</p>
          </div>
          <div>
            <p className="text-xs opacity-80">Taux appliqué</p>
            <p className="font-semibold tabular-nums">{avoirs.tauxRendement} %</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
        <div className="bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-lg p-4">
          <p className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wide">Versements enregistrés</p>
          <p className="text-lg font-semibold text-gray-900 dark:text-slate-100 tabular-nums mt-1">{activite.nbVersements}</p>
        </div>
        <div className="bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-lg p-4">
          <p className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wide">Dernier versement</p>
          <p className="text-lg font-semibold text-gray-900 dark:text-slate-100 mt-1">
            {activite.dernierVersement ? fmtDate(activite.dernierVersement.date) : '—'}
          </p>
          {activite.dernierVersement && (
            <p className="text-sm text-gray-500 dark:text-slate-400 tabular-nums">{fmtMontant(activite.dernierVersement.montant)}</p>
          )}
        </div>
        <div className="bg-white dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-lg p-4">
          <p className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wide">Périodes capitalisées</p>
          <p className="text-lg font-semibold text-gray-900 dark:text-slate-100 tabular-nums mt-1">{activite.nbPeriodesCapitalisees}</p>
        </div>
      </div>

      {/* Le rendement n'est pas garanti : le dire ici, pas en note de bas de page. */}
      <div className="flex items-start gap-3 mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
        <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          Le taux de {avoirs.tauxRendement} % est une <strong>perspective de rendement attendu</strong>,
          pas un engagement contractuel. Les intérêts affichés sont ceux effectivement
          capitalisés à ce jour.
        </span>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MES VERSEMENTS
// ═══════════════════════════════════════════════════════════════════════════

export const PortailFondsVersements: React.FC = () => {
  const { donnees, erreur, enCours, recharger } =
    useChargement<VersementFonds[]>(() => espaceClientService.fondsMesVersements(200), 'Versements indisponibles');

  if (enCours) return <Chargement />;
  if (erreur) return <Erreur message={erreur} onReessayer={recharger} />;

  const versements = donnees || [];
  const total = versements.reduce((t, v) => t + (Number(v.montant) || 0), 0);

  return (
    <div>
      <EnTete
        titre="Mes versements"
        sousTitre="Chaque ligne porte le libellé et la référence de votre virement, pour un rapprochement avec vos propres relevés de banque."
      />

      {versements.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-slate-400">
          <Landmark className="w-8 h-8 mx-auto text-gray-300" />
          <p className="mt-2 text-sm">Aucun versement enregistré à ce jour.</p>
        </div>
      ) : (
        <>
          <div className="border border-gray-200 dark:border-neutral-800 rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead className="bg-gray-50 dark:bg-neutral-900">
                <tr className="text-left text-xs text-gray-500 dark:text-slate-400 uppercase">
                  <th className="px-4 py-2.5 font-medium">Date de valeur</th>
                  <th className="px-4 py-2.5 font-medium">Libellé bancaire</th>
                  <th className="px-4 py-2.5 font-medium">Référence</th>
                  <th className="px-4 py-2.5 font-medium text-right">Montant</th>
                </tr>
              </thead>
              <tbody>
                {versements.map((v) => (
                  <tr key={v.id} className="border-t border-gray-100 dark:border-neutral-800">
                    <td className="px-4 py-2.5 whitespace-nowrap text-gray-600 dark:text-slate-300">
                      {fmtDate(v.dateValeur || v.dateOperation)}
                    </td>
                    <td className="px-4 py-2.5 text-gray-800 dark:text-slate-100">{v.libelleBancaire || '—'}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-gray-500 dark:text-slate-400">
                      {v.referenceBancaire || '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold tabular-nums whitespace-nowrap">
                      {fmtMontant(v.montant)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4 px-1 text-sm">
            <span className="text-gray-500 dark:text-slate-400">{versements.length} versement(s)</span>
            <span className="font-semibold text-gray-900 dark:text-slate-100 tabular-nums">
              Total versé : {fmtMontant(total)}
            </span>
          </div>

          <div className="flex items-start gap-3 mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900">
            <TrendingUp className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              Un versement manquant ? Il est probablement en attente d'identification : son
              libellé ne permettait pas de vous reconnaître avec certitude. Demandez à ce que
              votre <strong>numéro de souscripteur</strong> figure dans le libellé du virement.
            </span>
          </div>
        </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MES DOCUMENTS
// ═══════════════════════════════════════════════════════════════════════════

export const PortailFondsDocuments: React.FC = () => {
  const { donnees, erreur, enCours, recharger } =
    useChargement<DocumentFonds[]>(() => espaceClientService.fondsMesDocuments(), 'Documents indisponibles');

  if (enCours) return <Chargement />;
  if (erreur) return <Erreur message={erreur} onReessayer={recharger} />;

  const documents = donnees || [];

  const ouvrir = (doc: DocumentFonds) => {
    const url = espaceClientService.fondsUrlDocument(doc.type, doc.parametres?.dateArrete);
    window.open(url, '_blank', 'noopener');
  };

  return (
    <div>
      <EnTete
        titre="Mes documents"
        sousTitre="Attestation d'adhésion, relevés de situation et historique de vos versements."
      />

      {documents.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-slate-400">
          <FileText className="w-8 h-8 mx-auto text-gray-300" />
          <p className="mt-2 text-sm">Aucun document disponible pour le moment.</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-neutral-800 border-y border-gray-200 dark:border-neutral-800">
          {documents.map((doc, i) => (
            <div key={`${doc.type}-${i}`} className="flex items-center gap-4 py-4">
              <FileText className="w-5 h-5 text-blue-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-slate-100">{doc.libelle}</p>
                <p className="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-1.5 mt-0.5">
                  {doc.date && (
                    <>
                      <Calendar className="w-3 h-3" />
                      {fmtDate(doc.date)}
                    </>
                  )}
                  {!doc.date && 'Généré à la demande'}
                </p>
              </div>
              <button
                onClick={() => ouvrir(doc)}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm
                           border border-gray-300 dark:border-neutral-700 rounded-lg hover:bg-gray-50 text-gray-700 dark:text-slate-200"
              >
                <Download className="w-3.5 h-3.5" /> Ouvrir
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-start gap-3 mt-6 p-4 bg-gray-50 dark:bg-neutral-900 border border-gray-200 dark:border-neutral-800 rounded-lg text-sm text-gray-700 dark:text-slate-200">
        <Wallet className="w-4 h-4 shrink-0 mt-0.5 text-gray-400 dark:text-slate-500" />
        <span>
          Ces pièces sont celles que produit le service adhésions — identiques à celles qui
          vous seraient remises au guichet. Un relevé liste vos versements avec leur date de
          valeur et leur référence bancaire.
        </span>
      </div>
    </div>
  );
};
